import MessageRepository from "../domain/data/MessageRepository";
import { TargetAudience } from "../domain/entities/Campaign";
import Message from "../domain/entities/Message";
import User from "../domain/entities/User";
import AwsApiClient from "../presentation/hooks/AwsApiClient";

export default class APIMessageRepository implements MessageRepository {
  private client = new AwsApiClient();

  async saveMessage(user: User, message: Message): Promise<Message> {
    try {
      const response = await this.client.post(`/api/message`, message);
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getMessage(user: User, messageId: string): Promise<Message> {
    try {
      const response = await this.client.get(
        `/api/message/${user.organization.id}/${user.id}/${messageId}`
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getMessages(
    user: User,
    targetAudience: TargetAudience
  ): Promise<Message[]> {
    try {
      const response = await this.client.get(
        `/api/messages/${targetAudience}/${user.organization.id}/${user.id}`
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getMessagesbyLookup(
    user: User,
    targetAudience: TargetAudience,
    lookupType: string,
    lookupValue: string
  ): Promise<Message[]> {
    try {
      const response = await this.client.get(
        `/api/messages/${targetAudience}/${user.organization.id}/${user.id}/${lookupType}/${lookupValue}`
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getMessagesPagination(
    user: User,
    targetAudience: string,
    pageSize: number,
    lastEvaluatedKey: Record<string, string> | null
  ): Promise<{ items: Message[]; lastEvaluatedKey?: any }> {
    try {
      const requestBody = {
        pageSize,
        lastEvaluatedKey,
      };
      
      const response = await this.client.post(
        `/api/messages/pagination/${targetAudience}/${user.organization.id}/${user.id}`,
        requestBody
      );
      
      return response;
    } catch (error) {
      throw new Error(`An error has occurred: ${error}`);
    }
  }

  async getMessagesByLookupPagination(
    user: User,
    targetAudience: string,
    lookupType: string,
    lookupValue: string,
    pageSize: number,
    lastEvaluatedKey: Record<string, string> | null,
    existingMessageIds: string[] | null
  ): Promise<{ items: Message[]; lastEvaluatedKey?: any }> {
    try {
      const requestBody = {
        lookupType,
        lookupValue,
        pageSize,
        lastEvaluatedKey,
        existingMessageIds,
      };

      const response = await this.client.post(
        `/api/messages/lookup/pagination/${targetAudience}/${user.organization.id}/${user.id}`,
        requestBody
      );
      
      return response;
    } catch (error) {
      throw new Error(`An error has occurred: ${error}`);
    }
  }

  async getMessagesCount(user: User, targetAudience: string): Promise<number> {
    try {
      const response = await this.client.get(
        `/api/messages/count/${targetAudience}/${user.organization.id}/${user.id}`
      );
      return response;
    } catch (error) {
      throw new Error(`An error has occurred: ${error}`);
    }
  }

  async getMessagesLookupsCount(
    user: User,
    targetAudience: string,
    lookupType: string,
    lookupValue: string
  ): Promise<number> {
    try {
      const requestBody = { lookupType, lookupValue };
  
      const response = await this.client.post(
        `/api/messages/lookup/count/${targetAudience}/${user.organization.id}/${user.id}`,
        requestBody
      );
  
      return response;
    } catch (error) {
      const message = `An error has occurred: ${error}`;
      throw new Error(message);
    }
  }  

  async sendMessage(user: User, message: Message): Promise<string | undefined> {
    let endpointUrl = `/api/message/send/${user.organization.id}/${user.id}/${message.id}`;
    try {
      const response = await this.client.post(endpointUrl, null, "none");
      return message.id;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      console.log("Error: ", message);
      throw new Error(message);
    }
  }

  async getMessagesBySignatory(
    user: User,
    signatoryEmail: string
  ): Promise<Message[]> {
    try {
      const encodedSignatoryEmail = encodeURIComponent(signatoryEmail);
      const response = await this.client.get(
        `/api/messages/email/${user.organization.id}/${user.id}/${encodedSignatoryEmail}`
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async deleteMessage(
    user: User,
    message: Message,
    targetAudience: string
  ): Promise<boolean> {
    try {
      const response = await this.client.delete(
        `/api/message/${targetAudience}/${user.organization.id}/${user.id}/${message.id}`,
        "none"
      );
      return true;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getMessageEmailTracking(
    user: User,
    message: Message
  ): Promise<{ tracking: any; stats: any }> {
    try {
      const response = await this.client.get(
        `/api/email-tracking/message/${user.organization.id}/${message.id}`
      );
      console.log('📧 API Response - Message Email Tracking:', {
        url: `/api/email-tracking/message/${user.organization.id}/${message.id}`,
        response,
        trackingKeys: response.tracking ? Object.keys(response.tracking) : [],
        statsKeys: response.stats ? Object.keys(response.stats) : []
      });
      return response;
    } catch (error) {
      const message = `An error has occured fetching message email tracking: ${error}`;
      console.error(message);
      // Return empty tracking data instead of throwing - tracking is optional
      return { tracking: {}, stats: {} };
    }
  }
}
